import React, { useEffect, useState } from "react";
import { ExternalLink, ChevronDown, ChevronRight } from "lucide-react";
import api from "../lib/api";

/**
 * Stage-3 evidence-based report preview.
 *
 * Reads the shared /api/v1/audits/:id/report Report model — the same
 * shape the PDF is rendered from. Preview, PDF, and raw JSON therefore
 * reconcile for the same audit.
 *
 * Renders four dispositions:
 *   - kind="axe"         → executive summary, counts A/B/C, per-rule
 *                          finding cards with WCAG refs, selectors,
 *                          snippets, user impact, next actions.
 *   - kind="legacy"      → provenance banner + legacy findings (no
 *                          reinterpretation).
 *   - kind="scan_status" → clear "scan did not complete" panel — never
 *                          shows "clean" copy.
 *
 * A "View raw JSON" toggle links to the axe-json download endpoint. The
 * "Download PDF" button hits the existing PDF endpoint; both never move
 * outreach state (Stage-2 rule) — verified in test_stage2_batch.py.
 */
const OUTCOME_BADGE = {
  axe: { SUCCESS: ["Scan executed", "bg-emerald-50 border-emerald-300 text-emerald-900"],
         PARTIAL: ["Scan executed · partial coverage", "bg-amber-50 border-amber-400 text-amber-900"] },
  scan_status: { BLOCKED: ["Scan blocked", "bg-red-50 border-red-400 text-red-900"],
                 FAILED:  ["Scan failed",  "bg-red-50 border-red-400 text-red-900"] },
  legacy: { default: ["Legacy heuristic report", "bg-zinc-50 border-zinc-300 text-zinc-700"] },
};

function Badge({ report }) {
  const map = OUTCOME_BADGE[report.kind] || {};
  const [text, cls] = map[report.scan_outcome] || map.default || ["Report", "bg-zinc-50 border-zinc-300"];
  return (
    <span className={`inline-block px-2 py-0.5 text-[10px] font-black uppercase tracking-widest border ${cls}`}>
      {text}
    </span>
  );
}

function FindingCard({ f }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-zinc-950 mb-2 bg-white" data-testid={`report-rule-${f.rule_id}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full text-left px-3 py-2 flex items-center gap-2 hover:bg-zinc-50"
        aria-expanded={open}
      >
        {open ? <ChevronDown className="w-4 h-4 shrink-0" /> : <ChevronRight className="w-4 h-4 shrink-0" />}
        <span className="font-mono text-sm font-bold">{f.rule_id}</span>
        <span className="text-[10px] px-1.5 py-0.5 border border-zinc-950 uppercase tracking-widest">
          {f.impact || "n/a"}
        </span>
        <span className="text-xs text-zinc-700 truncate flex-1">{f.description || f.help_text}</span>
        <span className="text-xs font-mono font-bold">
          {f.instance_count} element{f.instance_count === 1 ? "" : "s"}
        </span>
      </button>
      {open && (
        <div className="border-t border-zinc-300 p-3 space-y-2 text-xs">
          {f.wcag_readable?.length ? (
            <div>
              <div className="uppercase tracking-widest text-[10px] text-zinc-500">WCAG</div>
              <div>{f.wcag_readable.join(" · ")}</div>
            </div>
          ) : null}
          <div>
            <div className="uppercase tracking-widest text-[10px] text-zinc-500">User impact</div>
            <div>{f.user_impact}</div>
          </div>
          <div>
            <div className="uppercase tracking-widest text-[10px] text-zinc-500">Suggested next action</div>
            <div>{f.next_action}</div>
          </div>
          {f.help_url && (
            <a
              href={f.help_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-xs underline"
            >
              Rule details on Deque University <ExternalLink className="w-3 h-3" />
            </a>
          )}
          <div>
            <div className="uppercase tracking-widest text-[10px] text-zinc-500 mb-1">
              Affected elements ({f.instance_count})
            </div>
            {(f.nodes || []).slice(0, 6).map((n, i) => (
              <div key={i} className="border border-zinc-300 mt-1 bg-zinc-50 p-2 space-y-1">
                <div className="font-mono text-[11px] text-purple-800 break-all">{n.selector || "(no selector)"}</div>
                {n.html && (
                  <pre className="bg-white border border-zinc-300 p-2 text-[11px] leading-snug whitespace-pre-wrap break-all">
                    {n.html.length > 400 ? n.html.slice(0, 400) + "…" : n.html}
                  </pre>
                )}
                {n.failure_summary && (
                  <div className="text-[11px] text-zinc-700 whitespace-pre-wrap">{n.failure_summary}</div>
                )}
              </div>
            ))}
            {f.nodes && f.nodes.length > 6 && (
              <div className="text-[10px] text-zinc-500 mt-1">
                +{f.nodes.length - 6} more — see raw JSON.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function Section({ id, findings }) {
  const label = {
    A: "Section A · WCAG failures",
    B: "Section B · Best-practice findings",
    C: "Section C · Manual review / incomplete",
    D_LEGACY: "Section D · Legacy heuristic findings",
  }[id] || id;
  return (
    <div className="mb-4" data-testid={`report-section-${id}`}>
      <div className="font-display text-sm font-bold uppercase tracking-widest mb-2">{label}</div>
      {findings.length ? findings.map((f) => <FindingCard key={f.rule_id} f={f} />) : (
        <div className="text-xs italic text-zinc-500">None in the tested scope.</div>
      )}
    </div>
  );
}

export default function AuditReportPreview({ auditId }) {
  const [report, setReport] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!auditId) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/v1/audits/${auditId}/report`);
        if (!cancelled) setReport(data);
      } catch (e) {
        if (!cancelled) setErr(e?.response?.data?.detail || "Failed to load report");
      }
    })();
    return () => { cancelled = true; };
  }, [auditId]);

  if (err) return <div className="text-red-700 text-sm" data-testid="report-error">{err}</div>;
  if (!report) return <div className="text-xs text-zinc-500">Loading report…</div>;

  const findingsBySection = (id) => (report.findings || []).filter((f) => f.section === id);
  const isAxe = report.kind === "axe";

  return (
    <div className="space-y-3" data-testid="audit-report-preview">
      <div className="flex items-start gap-3">
        <div>
          <div className="font-display text-lg font-black">{report.business_name}</div>
          <div className="font-mono text-xs break-all">{report.tested_url}</div>
        </div>
        <div className="ml-auto text-right space-y-1">
          <Badge report={report} />
          <div className="text-[10px] font-mono text-zinc-500">
            axe {report.axe_version || "—"} · {report.browser_version || "—"} · {report.duration_ms || 0}ms
          </div>
          <div className="text-[10px] font-mono text-zinc-500">
            {report.viewport ? `${report.viewport.width}×${report.viewport.height}` : ""}
          </div>
        </div>
      </div>

      {report.provenance_banner && (
        <div className="border border-zinc-400 bg-zinc-50 p-2 text-xs text-zinc-800" data-testid="report-provenance">
          {report.provenance_banner}
        </div>
      )}

      <div className="bg-zinc-50 border-l-4 border-zinc-950 p-3 text-sm" data-testid="report-executive-summary">
        {report.executive_summary}
      </div>

      {report.kind === "scan_status" ? (
        <div className="border-2 border-red-500 bg-red-50 p-3" data-testid="report-scan-status" role="alert">
          <div className="font-bold text-red-800 text-sm mb-1">
            Scan could not be completed; accessibility findings are unavailable.
          </div>
          <div className="text-xs text-red-800"><strong>Outcome:</strong> {report.scan_outcome}</div>
          <div className="text-xs text-red-800"><strong>Error:</strong> {report.scan_status_message}</div>
          <div className="text-[11px] text-red-800 mt-1 italic">
            This audit produced no findings evidence. Retry or hand-inspect the site — do not treat this as a clean result.
          </div>
        </div>
      ) : (
        <>
          {isAxe && report.section_summaries?.length > 0 && (
            <div className="flex flex-wrap gap-2" data-testid="report-counts">
              {report.section_summaries.map((s) => (
                <div key={s.id} className="border border-zinc-950 px-3 py-2 min-w-[130px]">
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500">Section {s.id}</div>
                  <div className="font-display text-2xl font-black">{s.instances}</div>
                  <div className="text-[10px] font-mono text-zinc-600">{s.rules} rule{s.rules === 1 ? "" : "s"} · {s.label}</div>
                </div>
              ))}
            </div>
          )}

          {isAxe ? (
            <>
              <Section id="A" findings={findingsBySection("A")} />
              <Section id="B" findings={findingsBySection("B")} />
              <Section id="C" findings={findingsBySection("C")} />
            </>
          ) : (
            <Section id="D_LEGACY" findings={findingsBySection("D_LEGACY")} />
          )}
        </>
      )}

      {report.limitations?.length > 0 && (
        <div className="border border-zinc-300 p-2" data-testid="report-limitations">
          <div className="font-display text-xs font-bold uppercase tracking-widest mb-1">Limitations &amp; tested scope</div>
          <ul className="text-xs space-y-1">
            {report.limitations.map((l, i) => (
              <li key={i}>
                <span className="uppercase tracking-widest text-[10px] text-zinc-500">{l.kind}</span> — {l.detail}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="border-t border-zinc-300 pt-2 text-[10px] font-mono text-zinc-500">
        Audit ID {report.audit_id} · generated {report.generated_at}
      </div>
    </div>
  );
}
